import { netSuiteConstants } from 'modules/common/constants';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { getCustomFieldMaxLength, sortCustomFields } from 'pages/requestForm/containers/netsuite/helpers';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { getRequestEditMode } from 'pages/requestForm/selectors/requestSelectors';
import { getRuleTypeByEditMode } from 'pages/requestForm/utils/helpers';
import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { useGetNetSuiteCustomRecords } from 'shared/data/webApp/v1';

import { messages } from './NetSuiteLineCustomField.messages';
import { NetSuiteLineCustomFieldProps, UseCustomFieldsColumnDefinitionProps } from './NetSuiteLineCustomField.types';

export const useCustomRecords = (props: { fieldId?: string; sublistType?: string; query?: string }) => {
    const { fieldId, sublistType, query } = props;

    const request = useSelector(requestSelector);
    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const ruleType = getRuleTypeByEditMode(editMode);

    const { data, isLoading } = useGetNetSuiteCustomRecords(
        {
            query: {
                requestId: request.id,
                companyId: request.companyId,
                ruleType,
                fieldId,
                query,
                sublistType,
                rowNum: 116,
                startFrom: 0,
            },
        },
        {
            enabled: Boolean(fieldId && sublistType),
            staleTime: 1000 * 60 * 5,
        }
    );

    const items = useMemo(() => {
        return (data?.values || []).map((item) => ({
            id: item.id,
            text: item.name,
        }));
    }, [data]);

    return {
        customRecords: items,
        isLoadingCustomRecords: isLoading,
    };
};

/**
 * Retrieves available custom fields for a given workflow
 */
export const useAvailableCustomFields = (props: UseCustomFieldsColumnDefinitionProps) => {
    const { customFields, workflowId } = props;

    return useMemo(() => {
        const filteredFields = customFields.filter((field) => {
            const isAppropriateLevel =
                field.netSuiteField?.level === 'Lines' || field.netSuiteField?.level === 'Header And Lines';
            const isActiveWorkflow = field.workflows?.some(
                (workflow) => workflow.workflowId === workflowId && workflow.isActive
            );

            return !field.isArchived && isAppropriateLevel && isActiveWorkflow;
        });

        return sortCustomFields(filteredFields);
    }, [customFields, workflowId]);
};

/**
 * Prepares items with default values from form state for a custom field
 */
export const useItems = (props: Pick<NetSuiteLineCustomFieldProps, 'cellProps' | 'field' | 'sublistType'>) => {
    const { cellProps, field, sublistType } = props;

    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');

    useDebounce(() => setDebouncedQuery(query), 500, [query]);

    const { customRecords, isLoadingCustomRecords: isLoadingItems } = useCustomRecords({
        fieldId: field.id,
        sublistType,
        query: debouncedQuery,
    });

    const getItems = useCallback(
        (fieldName: keyof domain.NetSuiteCustomField) => {
            const customField = cellProps.item.customFields?.find(
                (customField) => customField.companyFieldId === field.id
            );
            const fieldValue = customField?.[fieldName];

            if (
                !fieldValue ||
                typeof fieldValue !== 'object' ||
                (Array.isArray(fieldValue) ? !fieldValue.length : !('id' in fieldValue))
            ) {
                return customRecords;
            }

            // If the field value is an array, add all custom records that are not in the array
            if (Array.isArray(fieldValue)) {
                const existingIds = new Set(customRecords.map(({ id }) => id));
                const newItems = fieldValue.filter((item) => !existingIds.has(item.id));

                return newItems.length ? [...newItems, ...customRecords] : customRecords;
            }

            // If the field value is an object, add custom record that have the same id
            const isExistingItem = customRecords.some(({ id }) => id === fieldValue.id);

            return isExistingItem ? customRecords : [fieldValue, ...customRecords];
        },
        [cellProps, customRecords, field.id]
    );

    return {
        getItems,
        isLoadingItems,
        setQuery,
    };
};

/**
 * Retrieves general props for a custom field controls
 */
export const useControlProps = (
    props: Pick<NetSuiteLineCustomFieldProps, 'disabled' | 'field'> &
        Pick<ReturnType<typeof useItems>, 'isLoadingItems' | 'setQuery'>
) => {
    const { disabled, field, isLoadingItems, setQuery } = props;

    const generalProps = useMemo(
        () => ({
            disabled,
            size: 'small' as const,
        }),
        [disabled]
    );

    const numberFieldProps = useMemo(
        () => ({
            ...generalProps,
            max: netSuiteConstants.maxCustomNumericFieldAmount,
            min: -netSuiteConstants.maxCustomNumericFieldAmount,
            placeholder: messages.inputPlaceholder,
            bordered: false,
        }),
        [generalProps]
    );

    const inputProps = useMemo(
        () => ({
            ...generalProps,
            placeholder: messages.inputPlaceholder,
            changeOnBlur: true,
            maxLength: getCustomFieldMaxLength(field),
            bordered: false,
        }),
        [generalProps, field]
    );

    const selectProps = useMemo(
        () => ({
            ...generalProps,
            placeholder: messages.selectPlaceholder,
            bordered: false,
            onInputChange: setQuery,
            progress: isLoadingItems,
            itemNameKey: 'text' as const,
        }),
        [generalProps, setQuery, isLoadingItems]
    );

    return {
        generalProps,
        numberFieldProps,
        inputProps,
        selectProps,
    };
};
